import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Alert, Spinner, ListGroup, Form, Badge } from "react-bootstrap";
import {
  FaMicrophone,
  FaStop,
  FaCheck,
  FaUndo,
  FaPlus,
  FaTrash,
  FaEdit,
  FaSave,
  FaTimes,
} from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../../services/api";
import { addDoorsToBid } from "../../services/bidService";

/**
 * AddItemsToJobModal - Modal for adding items to a special job via voice
 */
const AddItemsToJobModal = ({ show, onHide, job, onItemsAdded }) => {
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingComplete, setRecordingComplete] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState("");

  // Processing state
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState("");

  // Items state
  const [parsedDoors, setParsedDoors] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editDescription, setEditDescription] = useState("");
  const [editDetails, setEditDetails] = useState([]);

  // UI state
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showRecordingUI, setShowRecordingUI] = useState(true);

  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null); // track stream so we can stop tracks on reset/close

  const estimateId = job?.estimate_id;
  const bidId = job?.bid_id;

  const isBusy = uploading || processing || saving;
  const canProcess = Boolean(audioBlob && estimateId);
  const canSave = Boolean(bidId && parsedDoors.length > 0 && editingIndex === null && !saving);

  const recordingPrompt = useMemo(() => {
    if (parsedDoors.length > 0) return "Record additional items to add to your list.";
    return "Press the button and describe the doors/items you want to add to this job.";
  }, [parsedDoors.length]);

  // Clean up created object URL
  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const stopStreamTracks = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const stopRecorderIfRecording = () => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state === "recording") {
      rec.stop();
    }
  };

  // Start recording
  const startRecording = async () => {
    setError(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      audioChunksRef.current = [];
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data?.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        try {
          const blob = new Blob(audioChunksRef.current, { type: "audio/wav" });
          const url = URL.createObjectURL(blob);

          setAudioBlob(blob);
          setAudioUrl(url);
          setIsRecording(false);
          setRecordingComplete(true);
        } finally {
          stopStreamTracks();
        }
      };

      mediaRecorder.start(1000);
      setIsRecording(true);
    } catch (err) {
      console.error("Error accessing microphone:", err);
      setError("Could not access microphone. Please check permissions.");
      stopStreamTracks();
      setIsRecording(false);
    }
  };

  // Stop recording
  const stopRecording = () => {
    stopRecorderIfRecording();
  };

  const clearRecordingOnly = () => {
    setAudioBlob(null);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl("");
    setRecordingComplete(false);
  };

  // Reset recording (clears everything)
  const resetRecording = () => {
    stopRecorderIfRecording();
    stopStreamTracks();

    clearRecordingOnly();
    setParsedDoors([]);
    setError(null);
    setShowRecordingUI(true);
  };

  // Record more - keeps existing items
  const recordMore = () => {
    stopRecorderIfRecording();
    stopStreamTracks();

    clearRecordingOnly();
    setError(null);
    setShowRecordingUI(true);
  };

  const setStatus = (msg) => setProcessingStatus(msg);

  // Process recording with AI
  const processRecording = async () => {
    if (!canProcess) {
      setError("No recording to process or missing estimate ID");
      return;
    }

    setUploading(true);
    setStatus("Uploading audio...");

    try {
      // Upload audio
      const audioFile = new File([audioBlob], "recording.wav", { type: "audio/wav" });
      const formData = new FormData();
      formData.append("audio", audioFile);
      formData.append("estimate_id", String(estimateId));

      const uploadResponse = await api.post("/audio/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const recordingId = uploadResponse.data?.id;
      if (!recordingId) throw new Error("Upload succeeded but no recording ID returned.");

      setUploading(false);
      setProcessing(true);

      setStatus("Transcribing audio...");
      await api.post(`/audio/${recordingId}/transcribe`);

      setStatus("Processing with AI...");
      const aiResponse = await api.post(`/audio/${recordingId}/process-with-ai`);

      const doors = aiResponse.data?.doors || [];
      if (!Array.isArray(doors) || doors.length === 0) {
        setError("No items could be extracted from the recording. Please try again with clearer descriptions.");
        return;
      }

      // Append safely using functional update (prevents stale state bugs)
      setParsedDoors((prev) => {
        const maxDoorNumber = prev.length > 0 ? Math.max(...prev.map((d) => d.door_number || 0)) : 0;
        const newDoors = doors.map((door, idx) => ({
          ...door,
          door_number: maxDoorNumber + idx + 1,
        }));
        toast.success(`Found ${newDoors.length} item(s)!`);
        return [...prev, ...newDoors];
      });

      // Hide recording UI and clear recording state after successful processing
      setShowRecordingUI(false);
      clearRecordingOnly();
    } catch (err) {
      console.error("Error processing recording:", err);
      setError("Failed to process recording. Please try again.");
    } finally {
      setUploading(false);
      setProcessing(false);
      setStatus("");
    }
  };

  // Start editing a door
  const startEditing = (index) => {
    const door = parsedDoors[index];
    setEditingIndex(index);
    setEditDescription(door?.description || `Door #${door?.door_number ?? index + 1}`);
    setEditDetails(Array.isArray(door?.details) ? door.details : []);
  };

  // Save editing
  const saveEditing = () => {
    if (editingIndex === null) return;

    setParsedDoors((prev) => {
      const updated = [...prev];
      updated[editingIndex] = {
        ...updated[editingIndex],
        description: editDescription,
        details: editDetails,
      };
      return updated;
    });

    setEditingIndex(null);
    setEditDescription("");
    setEditDetails([]);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingIndex(null);
    setEditDescription("");
    setEditDetails([]);
  };

  // Remove a door
  const removeDoor = (index) => {
    setParsedDoors((prev) => prev.filter((_, i) => i !== index));
  };

  // Add detail to editing door
  const addDetail = () => {
    setEditDetails((prev) => [...prev, ""]);
  };

  // Update detail
  const updateDetail = (detailIndex, value) => {
    setEditDetails((prev) => {
      const updated = [...prev];
      updated[detailIndex] = value;
      return updated;
    });
  };

  // Remove detail
  const removeDetail = (detailIndex) => {
    setEditDetails((prev) => prev.filter((_, i) => i !== detailIndex));
  };

  // Save all items to the job
  const saveItemsToJob = async () => {
    if (!bidId || parsedDoors.length === 0) {
      setError(`No items to save or missing bid ID (bid_id: ${bidId}, items: ${parsedDoors.length})`);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const result = await addDoorsToBid(bidId, parsedDoors);
      toast.success(`${result?.count ?? parsedDoors.length} item(s) added to job successfully!`);

      if (onItemsAdded) onItemsAdded(result);

      handleClose();
    } catch (err) {
      console.error("Error saving items to job:", err);
      setError("Failed to save items. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    stopRecorderIfRecording();
    stopStreamTracks();

    setIsRecording(false);
    setRecordingComplete(false);
    setAudioBlob(null);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl("");

    setUploading(false);
    setProcessing(false);
    setProcessingStatus("");
    setParsedDoors([]);
    setEditingIndex(null);
    setEditDescription("");
    setEditDetails([]);
    setError(null);
    setSaving(false);
    setShowRecordingUI(true);

    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaMicrophone className="me-2 text-warning" />
          Add Items via Voice
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Recording Section */}
        {!processing && !uploading && showRecordingUI && (
          <div className="text-center py-4">
            {!recordingComplete ? (
              <>
                <p className="text-muted mb-4">{recordingPrompt}</p>

                {!isRecording ? (
                  <Button
                    variant="danger"
                    size="lg"
                    onClick={startRecording}
                    className="rounded-circle"
                    style={{ width: 80, height: 80 }}
                    disabled={isBusy}
                  >
                    <FaMicrophone size={32} />
                  </Button>
                ) : (
                  <Button
                    variant="dark"
                    size="lg"
                    onClick={stopRecording}
                    className="rounded-circle pulse-animation"
                    style={{ width: 80, height: 80 }}
                    disabled={isBusy}
                  >
                    <FaStop size={32} />
                  </Button>
                )}

                {isRecording && (
                  <p className="text-danger mt-3">
                    <span className="recording-dot">●</span> Recording...
                  </p>
                )}

                {parsedDoors.length > 0 && !isRecording && (
                  <p className="text-muted mt-3 small">
                    You have {parsedDoors.length} item(s) already. Recording will add more.
                  </p>
                )}
              </>
            ) : (
              <>
                <p className="text-success mb-3">
                  <FaCheck className="me-2" />
                  Recording complete!
                </p>

                {audioUrl && (
                  <audio controls src={audioUrl} className="mb-3" style={{ maxWidth: "100%" }} />
                )}

                <div className="d-flex justify-content-center gap-2">
                  <Button variant="outline-secondary" onClick={resetRecording} disabled={isBusy}>
                    <FaUndo className="me-1" /> Re-record
                  </Button>
                  <Button variant="primary" onClick={processRecording} disabled={!canProcess || isBusy}>
                    <FaCheck className="me-1" /> Process Recording
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Processing Status */}
        {(uploading || processing) && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <p className="text-muted">{processingStatus}</p>
          </div>
        )}

        {/* Parsed Items Review */}
        {parsedDoors.length > 0 && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">
                Review Items <Badge bg="primary">{parsedDoors.length}</Badge>
              </h6>

              <Button variant="outline-secondary" size="sm" onClick={recordMore} disabled={isBusy}>
                <FaMicrophone className="me-1" /> Record More
              </Button>
            </div>

            <ListGroup>
              {parsedDoors.map((door, index) => (
                <ListGroup.Item key={index} className="py-3">
                  {editingIndex === index ? (
                    <div>
                      <Form.Group className="mb-2">
                        <Form.Label className="small fw-bold">Description</Form.Label>
                        <Form.Control
                          type="text"
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          placeholder="Door description..."
                        />
                      </Form.Group>

                      <Form.Label className="small fw-bold">Details/Line Items</Form.Label>
                      {editDetails.map((detail, dIdx) => (
                        <div key={dIdx} className="d-flex gap-2 mb-2">
                          <Form.Control
                            type="text"
                            value={detail}
                            onChange={(e) => updateDetail(dIdx, e.target.value)}
                            placeholder="Detail..."
                          />
                          <Button variant="outline-danger" size="sm" onClick={() => removeDetail(dIdx)} disabled={isBusy}>
                            <FaTrash />
                          </Button>
                        </div>
                      ))}

                      <Button variant="outline-secondary" size="sm" onClick={addDetail} className="mb-3" disabled={isBusy}>
                        <FaPlus className="me-1" /> Add Detail
                      </Button>

                      <div className="d-flex gap-2">
                        <Button variant="success" size="sm" onClick={saveEditing} disabled={isBusy}>
                          <FaSave className="me-1" /> Save
                        </Button>
                        <Button variant="outline-secondary" size="sm" onClick={cancelEditing} disabled={isBusy}>
                          <FaTimes className="me-1" /> Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <strong>Door #{door.door_number}</strong>
                        {door.description && <span className="text-muted ms-2">- {door.description}</span>}
                        {Array.isArray(door.details) && door.details.length > 0 && (
                          <ul className="mb-0 mt-2 small text-muted">
                            {door.details.map((detail, dIdx) => (
                              <li key={dIdx}>{detail}</li>
                            ))}
                          </ul>
                        )}
                      </div>

                      <div className="d-flex gap-1">
                        <Button variant="outline-primary" size="sm" onClick={() => startEditing(index)} disabled={isBusy}>
                          <FaEdit />
                        </Button>
                        <Button variant="outline-danger" size="sm" onClick={() => removeDoor(index)} disabled={isBusy}>
                          <FaTrash />
                        </Button>
                      </div>
                    </div>
                  )}
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={saving}>
          Cancel
        </Button>

        {parsedDoors.length > 0 && (
          <Button variant="success" onClick={saveItemsToJob} disabled={!canSave}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Saving...
              </>
            ) : (
              <>
                <FaPlus className="me-1" />
                Add {parsedDoors.length} Item(s) to Job
              </>
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default AddItemsToJobModal;
